/**
 * ReadModePresence — wer ist gerade hier, und in welchem Absatz?
 *
 * Alex 2026-09-30: „ich würde auch gerne die Cursor von den Menschen
 * sehen können, wenn mehrere am Portal verbunden sind." Im Edit-Modus
 * zeichnet CollaborationCaret echte Einfügemarken. Der Lesemodus rendert
 * Markdown zu HTML, dort gibt es keine Zeichenpositionen. Was es gibt:
 * dieselben Awareness-Zustände (`user`, `cursor`) auf demselben Yjs-Dokument.
 * Der Cursor ist eine relative Yjs-Position — aufgelöst gegen unser
 * Spiegel-Dokument ergibt sie den Absatz (XmlElement mit `block_id`), in
 * dem die Person gerade schreibt. Das reicht für ein Namensschild am
 * Absatz und eine Leiste „wer ist dabei".
 *
 * Leser ohne Cursor (andere Lesemodus-Tabs) erscheinen nur in der Leiste.
 * Dieselbe Person mit zwei Verbindungen (Lesemodus + Popover-Editor) wird
 * auf einen Eintrag zusammengezogen; der schreibende Zustand gewinnt.
 */

import { useEffect, useState } from 'react'
import * as Y from 'yjs'
import type { Awareness } from 'y-protocols/awareness'

interface AwarenessUser {
  id?: string
  name?: string
  color?: string
  mode?: 'read' | 'edit'
}

interface Peer {
  id: string
  name: string
  color: string
  writing: boolean
  blockId: string | null
}

interface ChipPos {
  key: string
  left: number
  top: number
  name: string
  color: string
}

/** Relative Cursor-Position → block_id des Absatzes (oder `_paragraph_N`). */
function blockIdFromCursor(
  ydoc: Y.Doc,
  cursor: { anchor?: unknown; head?: unknown } | null | undefined,
): string | null {
  if (!cursor || !cursor.head) return null
  try {
    const rel = Y.createRelativePositionFromJSON(cursor.head as Y.RelativePosition)
    const abs = Y.createAbsolutePositionFromRelativePosition(rel, ydoc)
    if (!abs) return null
    let node: Y.AbstractType<unknown> | null = abs.type
    // Vom XmlText hoch zum Absatz-Element = direktes Kind des Wurzel-
    // Fragments. Achtung: Y.XmlElement erbt von Y.XmlFragment, ein
    // instanceof-Test auf den Parent bricht deshalb eine Ebene zu frueh
    // ab. Die Wurzel erkennt man daran, dass sie selbst keinen Parent hat.
    while (node && node.parent && (node.parent as Y.AbstractType<unknown>).parent) {
      node = node.parent as Y.AbstractType<unknown>
    }
    if (!node || !node.parent) return null // Cursor steht direkt in der Wurzel
    if (node instanceof Y.XmlElement) {
      const bid = node.getAttribute('block_id')
      if (bid) return String(bid)
      const frag = node.parent as Y.XmlFragment | null
      if (frag) {
        let idx = 0
        for (const child of frag.toArray()) {
          if (child === node) return `_paragraph_${idx}`
          idx++
        }
      }
    }
  } catch {
    /* Position noch nicht auflösbar (Sync läuft) — dann kein Schild */
  }
  return null
}

export function ReadModePresence({
  awareness,
  ydoc,
  selfId,
  readModeRoot,
}: {
  awareness: Awareness | null
  ydoc: Y.Doc | null
  /** Eigene Identität — wird aus der Liste gefiltert. */
  selfId: string
  readModeRoot: HTMLElement | null
}) {
  const [peers, setPeers] = useState<Peer[]>([])
  const [chips, setChips] = useState<ChipPos[]>([])

  useEffect(() => {
    if (!awareness || !ydoc) return
    const recompute = () => {
      const byId = new Map<string, Peer>()
      for (const [clientId, state] of awareness.getStates() as Map<number, Record<string, unknown>>) {
        if (clientId === awareness.clientID) continue
        const u = (state.user || null) as AwarenessUser | null
        if (!u || !u.id || !u.name) continue
        if (u.id === selfId) continue
        const cursor = (state.cursor || null) as { anchor?: unknown; head?: unknown } | null
        const blockId = blockIdFromCursor(ydoc, cursor)
        const writing = u.mode !== 'read'
        const cand: Peer = {
          id: u.id,
          name: u.name,
          color: u.color || '#888',
          writing,
          blockId,
        }
        const prev = byId.get(u.id)
        // Schreibender Zustand / bekannter Absatz gewinnt gegen den
        // reinen Lese-Tab derselben Person.
        if (!prev || (!prev.blockId && cand.blockId) || (!prev.writing && cand.writing)) {
          byId.set(u.id, cand)
        }
      }
      setPeers(Array.from(byId.values()))
    }
    recompute()
    awareness.on('update', recompute)
    // Auch nach Doc-Updates neu aufloesen: ein Cursor kann auf Zeichen
    // zeigen, die unser Spiegel erst mit dem naechsten Sync kennt.
    let t: number | null = null
    const onDoc = () => {
      if (t != null) return
      t = window.setTimeout(() => {
        t = null
        recompute()
      }, 200)
    }
    ydoc.on('update', onDoc)
    return () => {
      awareness.off('update', recompute)
      ydoc.off('update', onDoc)
      if (t != null) window.clearTimeout(t)
    }
  }, [awareness, ydoc, selfId])

  // Namensschilder an den Absätzen positionieren (fixed, nachgemessen).
  useEffect(() => {
    const withBlock = peers.filter((p) => p.blockId)
    if (!readModeRoot || withBlock.length === 0) {
      queueMicrotask(() => setChips([]))
      return
    }
    const measure = () => {
      const next: ChipPos[] = []
      for (const p of withBlock) {
        const el = readModeRoot.querySelector(
          `.read-para[data-block-id="${cssEsc(p.blockId!)}"]`,
        ) as HTMLElement | null
        if (!el) continue
        const r = el.getBoundingClientRect()
        if (r.bottom < 0 || r.top > window.innerHeight) continue
        next.push({ key: p.id, left: r.right, top: r.top, name: p.name, color: p.color })
      }
      setChips((prev) =>
        prev.length === next.length &&
        prev.every((c, i) => c.key === next[i].key && c.left === next[i].left && c.top === next[i].top)
          ? prev
          : next,
      )
    }
    const raf = window.requestAnimationFrame(measure)
    const intv = window.setInterval(measure, 300)
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      window.cancelAnimationFrame(raf)
      window.clearInterval(intv)
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [peers, readModeRoot])

  if (peers.length === 0) return null

  return (
    <>
      <div className="read-presence" aria-live="polite">
        {peers.map((p) => (
          <span key={p.id} className="read-presence__peer" title={p.writing ? 'schreibt gerade' : 'liest mit'}>
            <span className="read-presence__dot" style={{ background: p.color }} />
            {p.name}
            <span className="read-presence__mode">{p.writing ? 'schreibt' : 'liest'}</span>
          </span>
        ))}
      </div>
      {chips.map((c) => (
        <span
          key={c.key}
          className="read-presence__chip"
          style={{ left: c.left, top: c.top, background: c.color }}
        >
          {c.name}
        </span>
      ))}
    </>
  )
}

function cssEsc(value: string): string {
  if (typeof CSS !== 'undefined' && CSS.escape) return CSS.escape(value)
  return value.replace(/["\\]/g, '\\$&')
}
